import { useEffect, useState } from "react";
import { Link, Outlet, useParams, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, MapPin, Pencil, CalendarPlus, Lightbulb, PackageCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { projectsApi, callSheetApi } from "@/lib/api";
import EditProjectDialog from "@/components/EditProjectDialog";
import ProjectSubNav from "@/components/ProjectSubNav";
import AddShootDateDialog from "@/components/AddShootDateDialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export default function ProjectShell() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [addShootOpen, setAddShootOpen] = useState(false);
  const [wrapOpen, setWrapOpen] = useState(false);
  // Per-project admin can edit project metadata (e.g. name, locations, job#).
  const isAdmin = project?.my_role === "admin";
  // Honor the previous page's breadcrumb (e.g. `/clients/{id}` when the
  // user arrived from a Client detail page). Falls back to All Projects
  // for deep-links / refreshes / arrivals from the Launchpad.
  const backTo = location.state?.from || "/";
  const backLabel = location.state?.fromLabel || "";

  const reloadProject = async () => {
    const proj = await projectsApi.get(id);
    setProject(proj);
    return proj;
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await reloadProject();
      } catch {
        toast.error("Failed to load project");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-6 h-6 text-amber-500 animate-spin" />
      </div>
    );
  }

  if (!project) {
    return <div className="p-12 text-zinc-400">Project not found.</div>;
  }

  return (
    <div className="min-h-screen">
      {/* Project header */}
      <header
        data-testid="project-header"
        className="border-b border-zinc-800/70 bg-zinc-950/60 backdrop-blur sticky top-0 z-30"
      >
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <Link
              to={backTo}
              data-testid="back-to-projects"
              title={backLabel ? `Back to ${backLabel}` : "Back to Projects"}
              className="p-2 rounded-sm text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <div>
              <div className="text-overline text-zinc-500">
                Production
                {project.job_number && (
                  <span className="ml-2 text-amber-500/80 font-mono">
                    · Job #{project.job_number}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <h1 className="font-heading text-2xl sm:text-3xl font-medium text-zinc-100 leading-tight">
                  {project.name}
                </h1>
                {isAdmin && (
                  <button
                    data-testid="edit-project-btn"
                    onClick={() => setEditOpen(true)}
                    title="Edit project (name, locations, date, director…)"
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-amber-500 hover:border-amber-500/40 rounded-sm transition-colors"
                  >
                    <Pencil className="w-3 h-3" /> Edit
                  </button>
                )}
                {isAdmin && project.status !== "completed" && project.status !== "archived" && (
                  <button
                    data-testid="complete-project-btn"
                    onClick={() => setWrapOpen(true)}
                    title="Complete this project — checks Gear, wraps every remaining day, and moves the project to Completed"
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/20 hover:border-emerald-500/60 rounded-sm transition-colors"
                  >
                    <PackageCheck className="w-3 h-3" /> Complete Project
                  </button>
                )}
                {project.status === "completed" && (
                  <span
                    data-testid="project-completed-pill"
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 rounded-sm font-mono uppercase tracking-wide"
                  >
                    <PackageCheck className="w-3 h-3" /> Completed
                  </span>
                )}
                {project.source_idea_id && (
                  <Link
                    to="/planning-calendar"
                    data-testid="project-source-idea-link"
                    title="View the source idea in the Planning Calendar"
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/20 rounded-sm transition-colors"
                  >
                    <Lightbulb className="w-3 h-3" /> Source Idea →
                  </Link>
                )}
              </div>
              {project.location && (
                <div className="text-xs text-zinc-500 mt-1.5 flex items-center gap-1.5">
                  <MapPin className="w-3 h-3 text-amber-500/70" />
                  {project.location}
                  {project.location_ids && project.location_ids.length > 1 && (
                    <span className="text-zinc-600">
                      +{project.location_ids.length - 1} more
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sub-navigation */}
        <ProjectSubNav projectId={id} projectMyRole={project.my_role} />
      </header>

      {/* Active tab */}
      <Outlet context={{ project, reloadProject }} />

      <EditProjectDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        project={project}
        onSaved={(updated) => {
          setProject(updated);
          setEditOpen(false);
        }}
        onDeleted={() => {
          setEditOpen(false);
          navigate("/projects", { replace: true });
        }}
      />

      <AddShootDateDialog
        open={addShootOpen}
        onOpenChange={setAddShootOpen}
        projectId={id}
        projectName={project.name}
        onAdded={async () => {
          // Refetch so day_count + shoot_date roll-ups reflect on the header / cards
          try { await reloadProject(); } catch { /* non-fatal */ }
        }}
      />

      <WrapProjectDialog
        open={wrapOpen}
        onOpenChange={setWrapOpen}
        projectId={id}
        projectName={project.name}
        onCompleted={async () => {
          setWrapOpen(false);
          try { await reloadProject(); } catch { /* non-fatal */ }
        }}
      />
    </div>
  );
}

function WrapProjectDialog({ open, onOpenChange, projectId, projectName, onCompleted }) {
  const [phase, setPhase] = useState("checking"); // checking | blocked | confirm | working
  const [gate, setGate] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setPhase("checking");
    setGate(null);
    (async () => {
      try {
        const status = await projectsApi.gearCheckoutStatus(projectId);
        if (cancelled) return;
        setGate(status);
        if (!status.gear_reachable || (status.checked_out_count || 0) > 0) {
          setPhase("blocked");
        } else {
          setPhase("confirm");
        }
      } catch (e) {
        if (cancelled) return;
        setGate({
          checked_out_count: 0,
          gear_reachable: false,
          error: e?.response?.data?.detail || e?.message || "Gear preflight failed",
        });
        setPhase("blocked");
      }
    })();
    return () => { cancelled = true; };
  }, [open, projectId]);

  const handleConfirm = async () => {
    setPhase("working");
    try {
      const res = await projectsApi.complete(projectId);
      toast.success(
        res.count > 0
          ? `Wrapped ${res.count} shoot day${res.count === 1 ? "" : "s"} · Project marked Completed`
          : "Project marked Completed",
      );
      onCompleted?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "gear_checked_out" || detail?.code === "gear_unreachable") {
        setGate({
          checked_out_count: detail.checked_out_count || 0,
          gear_reachable: detail.code !== "gear_unreachable",
          error: detail.gear_error || "",
          message: detail.message,
        });
        setPhase("blocked");
      } else {
        toast.error(detail?.message || detail || "Failed to complete project");
        setPhase("confirm");
      }
    }
  };

  const blocked = phase === "blocked";
  const checkedOut = gate?.checked_out_count || 0;
  const gearUnreachable = gate && gate.gear_reachable === false;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="complete-project-dialog"
        className="bg-zinc-950 border-zinc-800 text-zinc-100 sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-light flex items-center gap-2">
            <PackageCheck className="w-5 h-5 text-emerald-400" />
            Complete Project
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            Wrap every remaining day and move{" "}
            <span className="text-zinc-300 font-medium">{projectName}</span> to{" "}
            <span className="text-emerald-300">Completed</span>.
          </DialogDescription>
        </DialogHeader>

        {phase === "checking" && (
          <div
            data-testid="complete-gear-checking"
            className="flex items-center gap-3 rounded-sm border border-zinc-800 bg-zinc-900/40 px-4 py-4 text-sm text-zinc-400"
          >
            <Loader2 className="w-4 h-4 animate-spin text-amber-400" />
            Checking Gear check-out status…
          </div>
        )}

        {blocked && (
          <div
            data-testid="complete-gear-blocked-banner"
            className="rounded-sm border-2 border-rose-600 bg-rose-950/40 px-4 py-4 space-y-2"
          >
            <div className="flex items-center gap-2 text-rose-300 font-semibold">
              <AlertTriangle className="w-5 h-5" />
              Action Blocked
            </div>
            {gearUnreachable ? (
              <p className="text-sm text-rose-100/90 leading-relaxed">
                Cannot complete: the Gear app is unreachable. This action
                requires a confirmed check-out count for data integrity.
                Try again once Gear is back online.
              </p>
            ) : (
              <p className="text-sm text-rose-100/90 leading-relaxed">
                <span className="font-semibold">Action Blocked: Gear is still checked out.</span>{" "}
                Please return{" "}
                <span
                  data-testid="complete-gear-count"
                  className="font-mono text-rose-200 bg-rose-900/60 px-1.5 py-0.5 rounded"
                >
                  {checkedOut}
                </span>{" "}
                item{checkedOut === 1 ? "" : "s"} in the Gear App first.
              </p>
            )}
            {gate?.error ? (
              <p className="text-[11px] text-rose-300/70 font-mono break-all">
                {gate.error}
              </p>
            ) : null}
          </div>
        )}

        {(phase === "confirm" || phase === "working") && (
          <div
            data-testid="complete-confirm-body"
            className="rounded-sm border border-zinc-800 bg-zinc-900/40 px-4 py-4 text-sm text-zinc-300 leading-relaxed space-y-2"
          >
            <p>
              Every active shoot day will be archived and its Shoot Day task
              marked complete. Past PDFs and share links keep working under{" "}
              <strong className="text-zinc-100">Past Call Sheets</strong>.
            </p>
            <p className="text-zinc-400 text-xs">
              Gear is clear — the project status flips to Completed and the
              signal is sent to the Gear app.
            </p>
          </div>
        )}

        <DialogFooter className="pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={phase === "working"}
            className="bg-transparent border-zinc-800 text-zinc-300 hover:bg-zinc-900 hover:text-zinc-100"
          >
            {blocked ? "Close" : "Cancel"}
          </Button>
          {!blocked && phase !== "checking" && (
            <Button
              type="button"
              data-testid="complete-project-confirm-btn"
              onClick={handleConfirm}
              disabled={phase === "working"}
              className="bg-emerald-600 hover:bg-emerald-500 text-white gap-2"
            >
              {phase === "working" ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Completing…
                </>
              ) : (
                <>
                  <PackageCheck className="w-4 h-4" /> Complete Project
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
